import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { smsTemplatesDB, senderPhonesDB, type SmsTemplate, type SenderPhone } from '../../lib/smsTemplatesDB';

const MAX_MSG = 500;
type Tab = 'templates' | 'phones';

export default function SmsTemplatesPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('templates');

  // ── Templates state ──────────────────────────────────────────────────────
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // ── Sender phones state ───────────────────────────────────────────────────
  const [phones, setPhones] = useState<SenderPhone[]>([]);
  const [phoneLabel, setPhoneLabel] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [addingPhone, setAddingPhone] = useState(false);
  const [deletingPhoneId, setDeletingPhoneId] = useState<string | null>(null);

  const [successMsg, setSuccessMsg] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setTemplates(smsTemplatesDB.getAll());
    setPhones(senderPhonesDB.getAll());
  }, []);

  const notifySuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  // ── Template handlers ─────────────────────────────────────────────────────
  const openNew = () => {
    setEditingId(null);
    setFormName('');
    setFormMessage('');
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const openEdit = (t: SmsTemplate) => {
    setEditingId(t.id);
    setFormName(t.name);
    setFormMessage(t.message);
    setShowForm(true);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormName('');
    setFormMessage('');
  };

  const handleSaveTemplate = () => {
    if (!formName.trim() || !formMessage.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        smsTemplatesDB.update(editingId, { name: formName.trim(), message: formMessage.trim() });
        notifySuccess('Şablon güncellendi!');
      } else {
        smsTemplatesDB.insert({ name: formName.trim(), message: formMessage.trim() });
        notifySuccess('Şablon eklendi!');
      }
      setTemplates(smsTemplatesDB.getAll());
      closeForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = (id: string) => setDeletingId(id);

  const confirmDeleteTemplate = () => {
    if (!deletingId) return;
    smsTemplatesDB.delete(deletingId);
    setTemplates(smsTemplatesDB.getAll());
    setDeletingId(null);
    notifySuccess('Şablon silindi!');
  };

  const handleCopy = (msg: string, id: string) => {
    navigator.clipboard.writeText(msg).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const canSaveTemplate = formName.trim().length > 0 && formMessage.trim().length > 0;

  // ── Phone handlers ────────────────────────────────────────────────────────
  const handleAddPhone = () => {
    if (!phoneNumber.trim()) return;
    senderPhonesDB.insert({ label: phoneLabel.trim() || phoneNumber.trim(), number: phoneNumber.trim() });
    setPhones(senderPhonesDB.getAll());
    setPhoneLabel('');
    setPhoneNumber('');
    setAddingPhone(false);
    notifySuccess('Numara eklendi!');
  };

  const confirmDeletePhone = () => {
    if (!deletingPhoneId) return;
    senderPhonesDB.delete(deletingPhoneId);
    setPhones(senderPhonesDB.getAll());
    setDeletingPhoneId(null);
    notifySuccess('Numara silindi!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-purple-50">
      {/* NAV */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
              <i className="ri-message-2-line text-lg text-white"></i>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Mesaj Şablonları</h1>
              <p className="text-xs text-gray-500">Şablon ve gönderici numaraları yönetin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'templates' && (
              <button
                type="button"
                onClick={openNew}
                className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-add-line text-lg"></i>Yeni Şablon
              </button>
            )}
            {activeTab === 'phones' && (
              <button
                type="button"
                onClick={() => setAddingPhone(true)}
                className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-sim-card-line text-lg"></i>Numara Ekle
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i>Geri
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="max-w-4xl mx-auto px-6 flex gap-0 border-t border-gray-100">
          {([
            { key: 'templates', label: 'Şablonlar', icon: 'ri-file-text-line', count: templates.length },
            { key: 'phones', label: 'Gönderici Numaralar', icon: 'ri-sim-card-line', count: phones.length },
          ] as { key: Tab; label: string; icon: string; count: number | null }[]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-violet-500 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
              {tab.count !== null && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.key ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* SUCCESS TOAST */}
      {successMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-500 text-white rounded-xl flex items-center gap-2 text-sm shadow-lg">
          <i className="ri-check-line text-xl"></i>{successMsg}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 py-10">

        {/* ─── TEMPLATES TAB ─── */}
        {activeTab === 'templates' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Şablonlarım</h2>
                <p className="text-sm text-gray-500 mt-0.5">{templates.length} şablon kayıtlı</p>
              </div>
            </div>

            {templates.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                <div className="w-20 h-20 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-message-2-line text-4xl text-violet-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz şablon yok</h3>
                <p className="text-gray-500 text-sm mb-6">İlk SMS şablonunuzu oluşturun</p>
                <button type="button" onClick={openNew}
                  className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 transition-all inline-flex items-center gap-2 cursor-pointer text-sm">
                  <i className="ri-add-line text-lg"></i>Şablon Oluştur
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {templates.map((t) => (
                  <div key={t.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-violet-200 transition-colors">
                    <div className="px-5 py-4 flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 flex-shrink-0 bg-violet-50 rounded-xl flex items-center justify-center mt-0.5">
                          <i className="ri-file-text-line text-violet-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1">{t.name}</h3>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed">{t.message}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-gray-400">{t.message.length} karakter</span>
                            <span className="text-xs text-gray-300">•</span>
                            <span className="text-xs text-gray-400">{new Date(t.updatedAt).toLocaleDateString('tr-TR')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button type="button" onClick={() => handleCopy(t.message, t.id)} title="Mesajı kopyala"
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${copiedId === t.id ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-violet-600 hover:bg-violet-50'}`}>
                          <i className={`text-sm ${copiedId === t.id ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
                        </button>
                        <button type="button" onClick={() => openEdit(t)} title="Düzenle"
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-all cursor-pointer">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button type="button" onClick={() => handleDeleteTemplate(t.id)} title="Sil"
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── PHONES TAB ─── */}
        {activeTab === 'phones' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Gönderici Numaralar</h2>
              <p className="text-sm text-gray-500 mt-0.5">Kullanacağınız gönderici telefon numaralarını ekleyebilirsiniz.</p>
            </div>

            {phones.length === 0 && !addingPhone ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                <div className="w-20 h-20 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-sim-card-line text-4xl text-violet-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz numara yok</h3>
                <p className="text-gray-500 text-sm mb-6">İletişim numaralarınızı buraya kaydedin.</p>
                <button type="button" onClick={() => setAddingPhone(true)}
                  className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg font-medium hover:from-violet-600 hover:to-purple-700 transition-all inline-flex items-center gap-2 cursor-pointer text-sm">
                  <i className="ri-add-line text-lg"></i>Numara Ekle
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Add phone form */}
                {addingPhone && (
                  <div className="bg-white rounded-2xl border border-violet-200 p-5 space-y-4">
                    <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                      <i className="ri-add-circle-line text-violet-500"></i>Yeni Numara
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Etiket (isteğe bağlı)</label>
                        <input
                          type="text"
                          value={phoneLabel}
                          onChange={(e) => setPhoneLabel(e.target.value)}
                          placeholder="örn: Klinik Hattı"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefon Numarası/Başlık <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="örn: 0555..."
                          autoFocus
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={() => { setAddingPhone(false); setPhoneLabel(''); setPhoneNumber(''); }}
                        className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 cursor-pointer">
                        İptal
                      </button>
                      <button type="button" onClick={handleAddPhone} disabled={!phoneNumber.trim()}
                        className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${phoneNumber.trim() ? 'bg-violet-500 text-white hover:bg-violet-600 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                        <i className="ri-save-line"></i>Kaydet
                      </button>
                    </div>
                  </div>
                )}

                {phones.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl border border-gray-100 px-5 py-4 flex items-center justify-between hover:border-violet-200 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-sim-card-line text-violet-600"></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{p.label}</p>
                        <p className="text-sm text-gray-500 font-mono">{p.number}</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => setDeletingPhoneId(p.id)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* TEMPLATE ADD/EDIT MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-base">{editingId ? 'Şablonu Düzenle' : 'Yeni Şablon'}</h2>
                <p className="text-violet-200 text-xs mt-0.5">{editingId ? 'Şablon bilgilerini güncelleyin' : 'Yeni SMS şablonu oluşturun'}</p>
              </div>
              <button type="button" onClick={closeForm}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Şablon Adı <span className="text-red-400">*</span></label>
                <input ref={nameRef} type="text" value={formName} onChange={(e) => setFormName(e.target.value)}
                  placeholder="örn: Randevu Hatırlatma"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all" maxLength={100} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-gray-700">Mesaj <span className="text-red-400">*</span></label>
                  <span className={`text-xs font-medium ${formMessage.length > MAX_MSG * 0.9 ? 'text-orange-500' : 'text-gray-400'}`}>{formMessage.length}/{MAX_MSG}</span>
                </div>
                <textarea value={formMessage} onChange={(e) => setFormMessage(e.target.value.slice(0, MAX_MSG))}
                  placeholder="SMS mesaj içeriğini yazın..." rows={6}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all resize-none" />
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button type="button" onClick={closeForm}
                className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all cursor-pointer text-sm">İptal</button>
              <button type="button" onClick={handleSaveTemplate} disabled={!canSaveTemplate || saving}
                className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${canSaveTemplate && !saving ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                {saving ? 'Kaydediliyor...' : editingId ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEMPLATE DELETE CONFIRM */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDeletingId(null)}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Şablonu Sil</h3>
            <p className="text-sm text-gray-500 mb-6">Bu şablonu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeletingId(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all cursor-pointer text-sm">Vazgeç</button>
              <button type="button" onClick={confirmDeleteTemplate}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all cursor-pointer text-sm">Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* PHONE DELETE CONFIRM */}
      {deletingPhoneId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setDeletingPhoneId(null)}>
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-sim-card-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">Numarayı Sil</h3>
            <p className="text-sm text-gray-500 mb-6">Bu numarayı listeden kaldırmak istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDeletingPhoneId(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all cursor-pointer text-sm">Vazgeç</button>
              <button type="button" onClick={confirmDeletePhone}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all cursor-pointer text-sm">Sil</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
